import StatusGlyph from "@/Components/Passport/StatusGlyph";
import { normalizeStatus, PUBLISH_STATUS_LABELS } from "@/lib/publishStatus";
import { shortDate } from "@/lib/serverTime";
import { cn } from "@/lib/utils";
import { RailRow } from "./primitives";

/**
 * The passport overview's right rail (C12A): a completeness card, then the
 * passport's status, last release, schema, creation date and open requests.
 *
 * - `setCount` / `fieldCount`: values set, of the level's properties.
 *   Completeness is their ratio (100% for a level without properties).
 * - `onShowMissing()`: the card's "show" link (the editor filters the list to
 *   the missing values and scrolls to it).
 * - `status`: `passport.publish_status`; an absent one (null, or no key on
 *   a passport stored without one) reads as draft.
 * - `lastRelease`: the changelog entry that made the latest release, if
 *   loaded (see latestRelease in ./changelog); `releaseNumber` stands in
 *   when it is not.
 * - `schemaVersion` / `latestVersion`: the pinned and newest ontology version.
 * - `createdAt`: `passport.meta.created_at`, read in `timeZone`.
 * - `openRequests`: the open data request count, or null to leave the row
 *   out; `onOpenRequests()` is the count's link.
 * - `dayFirst`: dates day first ("11 Sep"), for the marketing site's
 *   vignettes (1d); off by default.
 */
export default function SummaryRail({
    setCount,
    fieldCount,
    onShowMissing,
    status,
    lastRelease = null,
    releaseNumber = 0,
    schemaVersion = null,
    latestVersion = null,
    createdAt = null,
    timeZone,
    openRequests = null,
    onOpenRequests,
    dayFirst = false,
}) {
    const completeness = fieldCount ? Math.round((setCount / fieldCount) * 100) : 100;
    const shownStatus = normalizeStatus(status);
    const missingCount = fieldCount - setCount;

    return (
        <aside aria-label="Passport summary" className="space-y-4 text-[12.5px]">
            <div className="rounded-lg bg-white p-4 ring-1 ring-zinc-950/[.07]">
                <p className="text-[11.5px] font-medium text-zinc-500">Completeness</p>
                <p className="mt-1 text-[20px] font-semibold tracking-tight tabular-nums text-zinc-950">
                    {completeness}<span className="text-[14px] text-zinc-400">%</span>
                </p>
                <div className="mt-2 h-1 overflow-hidden rounded-full bg-zinc-100" role="progressbar" aria-valuenow={completeness} aria-valuemin={0} aria-valuemax={100} aria-label="Values set">
                    <div className={cn('h-full rounded-full', completeness === 100 ? 'bg-emerald-500' : 'bg-zinc-800')} style={{ width: `${completeness}%` }} />
                </div>
                <p className="mt-2 text-zinc-500">
                    {missingCount > 0 ? (
                        <>
                            {missingCount} {missingCount === 1 ? 'value' : 'values'} missing ·{' '}
                            <button
                                type="button"
                                onClick={() => onShowMissing?.()}
                                className="font-medium text-zinc-900 underline-offset-2 hover:underline"
                            >
                                show
                            </button>
                        </>
                    ) : 'Every property has a value.'}
                </p>
            </div>
            <dl className="space-y-2.5 rounded-lg bg-white p-4 ring-1 ring-zinc-950/[.07]">
                <RailRow label="Status">
                    <span className="inline-flex items-center gap-1.5">
                        <StatusGlyph status={shownStatus} decorative />
                        {PUBLISH_STATUS_LABELS[shownStatus] ?? shownStatus}
                    </span>
                </RailRow>
                {(lastRelease || releaseNumber > 0) && (
                    <RailRow label="Last release">
                        <span className="tabular-nums" title={lastRelease?.updated_at}>
                            #{lastRelease?.release_number ?? releaseNumber}
                            {lastRelease?.updated_at && <> · {shortDate(lastRelease.updated_at, timeZone, undefined, { dayFirst })}</>}
                        </span>
                    </RailRow>
                )}
                {schemaVersion != null && (
                    <RailRow label="Schema">
                        <span className="tabular-nums">v{schemaVersion}{latestVersion ? ` of ${latestVersion}` : ''}</span>
                    </RailRow>
                )}
                {createdAt && (
                    <RailRow label="Created">
                        <span className="tabular-nums" title={createdAt}>{shortDate(createdAt, timeZone, undefined, { dayFirst })}</span>
                    </RailRow>
                )}
                {openRequests !== null && (
                    <RailRow label="Open requests">
                        <button type="button" onClick={() => onOpenRequests?.()} className="tabular-nums underline-offset-2 hover:underline">
                            {openRequests}
                        </button>
                    </RailRow>
                )}
            </dl>
        </aside>
    );
}
